import { Component, type ErrorInfo, type ReactNode } from "react";
import { StateMessage } from "./StateMessage";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Top-level crash guard. One broken screen (e.g. a malformed API payload
 * during the booth demo) must not take down the whole tab — the operator
 * needs a "reload" affordance, not a blank white page mid-pitch.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("EasyFarm Cloud demo crashed:", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div style={{ padding: "var(--space-8)" }}>
          <StateMessage
            tone="error"
            title="Something went wrong."
            detail={this.state.error.message}
            action={
              <button className="btn btn--primary" onClick={() => window.location.reload()}>
                Reload
              </button>
            }
          />
        </div>
      );
    }
    return this.props.children;
  }
}
